import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
} from '@material-ui/core';
import { useFormikContext } from 'formik';
import { IMobileAppSetting, ISettingsGroups } from '../interfaces';

interface IProps {
  disabled?: boolean;
}

type VersionKey =
  | 'MOBILE_ANDROID_VERSION'
  | 'MOBILE_ANDROID_MAX_VERSION'
  | 'MOBILE_IOS_VERSION'
  | 'MOBILE_IOS_MAX_VERSION';

// Свои короткие подписи: описания настроек с бэка длинные и в половину ширины не помещаются
const VERSION_LABELS: Record<VersionKey, string> = {
  MOBILE_ANDROID_VERSION: 'Минимальная версия Android',
  MOBILE_ANDROID_MAX_VERSION: 'Максимальная версия Android',
  MOBILE_IOS_VERSION: 'Минимальная версия iOS',
  MOBILE_IOS_MAX_VERSION: 'Максимальная версия iOS',
};

const VERSION_KEYS = new Set<string>(Object.keys(VERSION_LABELS));

// Максимум '0' на бэке - без ограничения, в поле он показывается пустым
const MAX_KEYS = new Set<VersionKey>(['MOBILE_ANDROID_MAX_VERSION', 'MOBILE_IOS_MAX_VERSION']);
const NO_LIMIT = '0';

// Только цифры и точки
const toVersion = (value: string) => value.replaceAll(/[^\d.]/g, '');

const MobileAppSettings = ({ disabled }: IProps) => {
  const { values, setFieldValue } = useFormikContext<{ settings: ISettingsGroups }>();

  const { groupName, settings } = values.settings[6];

  const find = <K extends IMobileAppSetting['key']>(key: K) => {
    const index = settings.findIndex((setting) => setting.key === key);
    if (index === -1) return null;

    return {
      ...(settings[index] as Extract<IMobileAppSetting, { key: K }>),
      field: `settings[6].settings[${index}].value`,
    };
  };

  const CHECK_LIST_BEFORE_AFTER_ENABLED = find('CHECK_LIST_BEFORE_AFTER_ENABLED');
  const MOBILE_LEGACY_BLOCKED = find('MOBILE_LEGACY_BLOCKED');
  const hasVersions = settings.some(({ key }) => VERSION_KEYS.has(key));

  const versionField = (key: VersionKey) => {
    const version = find(key);
    if (!version) return null;

    const isMax = MAX_KEYS.has(key);

    return (
      <TextField
        variant="filled"
        label={VERSION_LABELS[key]}
        value={isMax && version.value === NO_LIMIT ? '' : version.value}
        placeholder={isMax ? 'Без ограничения' : undefined}
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
        onChange={(event) => {
          const value = toVersion(event.target.value);
          setFieldValue(version.field, isMax && !value ? NO_LIMIT : value);
        }}
        fullWidth
      />
    );
  };

  return (
    <Card>
      <CardHeader title={groupName} />
      <Divider />

      <CardContent component={Box} sx={{ display: 'flex', flexDirection: 'column', gridGap: 16 }}>
        {CHECK_LIST_BEFORE_AFTER_ENABLED && (
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                color="primary"
                checked={CHECK_LIST_BEFORE_AFTER_ENABLED.value}
                onChange={(e) => setFieldValue(CHECK_LIST_BEFORE_AFTER_ENABLED.field, e.target.checked)}
              />
            }
            label={CHECK_LIST_BEFORE_AFTER_ENABLED.description}
          />
        )}

        {hasVersions && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Допустимые версии
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gridGap: 16 }}>
              <Box sx={{ display: 'flex', gridGap: 16 }}>
                {versionField('MOBILE_ANDROID_VERSION')}
                {versionField('MOBILE_ANDROID_MAX_VERSION')}
              </Box>

              <Box sx={{ display: 'flex', gridGap: 16 }}>
                {versionField('MOBILE_IOS_VERSION')}
                {versionField('MOBILE_IOS_MAX_VERSION')}
              </Box>
            </Box>

            <FormHelperText>
              Версия x.y.z, как в магазине приложений. Приложение с версией ниже минимальной или выше максимальной
              просит обновиться. Пустой максимум - без ограничения.
            </FormHelperText>
          </Box>
        )}

        {MOBILE_LEGACY_BLOCKED && (
          <Box>
            <FormControlLabel
              disabled={disabled}
              control={
                <Checkbox
                  color="primary"
                  checked={MOBILE_LEGACY_BLOCKED.value}
                  onChange={(e) => setFieldValue(MOBILE_LEGACY_BLOCKED.field, e.target.checked)}
                />
              }
              label={MOBILE_LEGACY_BLOCKED.description}
            />
            <FormHelperText>
              Версии, выпущенные до этой проверки, не сообщают свою версию - флажок не пускает их совсем. Включать,
              когда почти все обновятся.
            </FormHelperText>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MobileAppSettings;
